/**
 * PGSS-5161: the app's one logger, with a level that can be raised in a live session.
 *
 * Levels, most to least severe: error, warn, info, debug.
 *   - `error` and `warn` always emit.
 *   - `info` and `debug` emit only when the active level allows them.
 *
 * The active level is read on every call, from the first valid value of:
 *   1. localStorage `psg.logLevel` -- support or a developer sets this in the browser console
 *      (`localStorage.setItem('psg.logLevel', 'debug')`) to see more in any environment, no deploy;
 *   2. `REACT_APP_LOG_LEVEL` at build time;
 *   3. `'error'`.
 *
 * `logger.debug` is for a diagnostic someone would deliberately switch on to answer a specific
 * question. It is NOT for tracing values while developing: `logger.debug('inside')` or
 * `logger.debug('media', media)` is as useless behind a switch as it was as a bare console.log
 * (PR #2369 removed 52 of those). Remove them before merge.
 *
 * Production builds strip bare `console.log` / `console.info` / `console.debug` calls (esbuild
 * `pure` in vite.config.mjs). The calls below go through `sink`, not `console.*` by name, so the
 * strip cannot see them -- write them as `console.info(...)` and the runtime switch silently stops
 * working in production, the one place it is needed.
 */

export const LOG_LEVELS = ['error', 'warn', 'info', 'debug'];
export const LOG_LEVEL_STORAGE_KEY = 'psg.logLevel';
const DEFAULT_LEVEL = 'error';

const sink = globalThis.console;

const isLevel = (value) => LOG_LEVELS.includes(value);

function storedLevel() {
  try {
    return globalThis.localStorage?.getItem(LOG_LEVEL_STORAGE_KEY);
  } catch {
    // Storage can throw (blocked site data, private mode); that is not a reason to stop logging.
    return null;
  }
}

function configuredLevel() {
  try {
    return process.env.REACT_APP_LOG_LEVEL;
  } catch {
    // vite.config.mjs defines only the REACT_APP_* keys a build actually has. Guarded so an unset
    // key can never throw where `process` does not exist -- logging must not break the caller.
    return undefined;
  }
}

export function getLogLevel() {
  const stored = storedLevel()?.trim().toLowerCase();
  if (isLevel(stored)) {
    return stored;
  }
  const configured = configuredLevel()?.trim().toLowerCase();
  if (isLevel(configured)) {
    return configured;
  }
  return DEFAULT_LEVEL;
}

const enabled = (level) => LOG_LEVELS.indexOf(level) <= LOG_LEVELS.indexOf(getLogLevel());

const logger = {
  error: (...args) => sink.error(...args),
  warn: (...args) => sink.warn(...args),
  info: (...args) => {
    if (enabled('info')) {
      sink.info(...args);
    }
  },
  debug: (...args) => {
    if (enabled('debug')) {
      sink.debug(...args);
    }
  },
};

export default logger;
